import { useEffect, useState } from 'react';
import usePermissions from './hooks/usePermissions';
import { useCurrentUserProfile } from './hooks/user';
import { syncLanguageWithBackend } from './i18n';
import {
  DEFAULT_MODULE_ACCESS,
  ALL_MODULES_GRANTED,
  PERMISSION_VALUE_ALL,
} from './utils/moduleConstants';

function useGlobalPermissions() {
  const { data, isLoading: userProfileLoading } = useCurrentUserProfile();
  const { permissions: featurePermissions } = usePermissions('feature');
  const { permissions: dataPermissions } = usePermissions('data_access');

  const [userData, setUserData] = useState(() => JSON.parse(localStorage.getItem('permissions')));
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (!userProfileLoading && data) {
      setUserData(data);
      localStorage.setItem('permissions', JSON.stringify(data));

      if (data.data?.app_language) {
        syncLanguageWithBackend(data.data.app_language);
      }
    }
  }, [data, userProfileLoading]);

  const [dataAccessPermissions, setDataAccessPermissions] = useState({
    people: false,
    pay: false,
  });

  const [moduleAccessPermissions, setModuleAccessPermissions] = useState({
    ...DEFAULT_MODULE_ACCESS,
  });

  useEffect(() => {
    if (featurePermissions && dataPermissions) {
      const allFeaturePermissions = featurePermissions?.filter(
        (permission) => permission.permission_value === PERMISSION_VALUE_ALL
      );
      const allDataPermissions = dataPermissions?.filter(
        (permission) => permission.permission_value === PERMISSION_VALUE_ALL
      );

      if (allFeaturePermissions?.length > 0) {
        setModuleAccessPermissions({ ...ALL_MODULES_GRANTED });
      } else {
        const moduleAccess = {
          ...DEFAULT_MODULE_ACCESS,
          ...Object.fromEntries(featurePermissions.map((p) => [p.permission_value, true])),
        };

        setModuleAccessPermissions(moduleAccess);
      }

      if (allDataPermissions?.length > 0) {
        setDataAccessPermissions({
          people: true,
          pay: true,
        });
      } else {
        const dataAccess = Object.keys(dataAccessPermissions).reduce(
          (acc, permission) => ({
            ...acc,
            [permission]: dataPermissions?.some((perm) => perm.permission_value === permission),
          }),
          {}
        );

        setDataAccessPermissions(dataAccess);
      }

      setPermissionsLoaded(true);
    }
  }, [userData, featurePermissions, dataPermissions]);

  return {
    userData,
    userProfileLoading,
    dataAccessPermissions,
    moduleAccessPermissions,
    permissionsLoaded,
  };
}

export default useGlobalPermissions;
